import { date as dateUtil, price, string } from '@eti/utils';

export const isInBound = (content, bound, index) =>
  content.length === 2 && index === 1 && bound.segments.length > 0;

export const isOutBound = (content, bound, index) =>
  (content.length === 1 || content.length === 2) && index === 0 && bound.segments.length > 0;

// TODO: implement proper check for multi trip https://jira.etraveli.net/browse/SBK-28
export const isMultiStop = (content, bound) => content.length > 2 && bound.segments.length > 0;

const getMatchingSegmentId = (changedSegments, segment) =>
  changedSegments.find((x) => x.segmentId === segment.segmentId);

export const getBoundTitle = (segments) =>
  `${segments[0].origin.cityName} – ${segments[segments.length - 1].destination.cityName}`;

export const getTripScheduleChanges = (changedSegments, bounds) => {
  const changedBounds = bounds.map(({ segments }) => ({
    segments: segments.reduce((acc, segment) => {
      const matchingSegment = getMatchingSegmentId(changedSegments, segment);
      const { origin, destination, flightNumber } = segment;
      if (matchingSegment) {
        acc.push({
          ...matchingSegment,
          flightNumber,
          originCityCode: origin.cityCode,
          originCityName: origin.cityName,
          destinationCityCode: destination.cityCode,
          destinationCityName: destination.cityName,
        });
      }

      return acc;
    }, []),
  }));
  const noChangedBounds = changedBounds[0].segments.length === 0 && bounds.length === 1;

  return noChangedBounds ? false : changedBounds;
};

const getTripPriceChange = ({ priceDiff, priceNew, priceOld }) => ({
  priceDiff: priceDiff?.price?.value,
  priceNew: priceNew.price.value,
  priceOld: priceOld.price.value,
  currency: priceNew.price.currency.code,
});

export const getChangesByTypename = (typename, changes) =>
  changes ? changes.filter(({ __typename }) => __typename === typename) : [];

export const getTripNotificationChanges = (changes, bounds) => {
  const priceChanges = getChangesByTypename('TripPriceChange', changes);
  const scheduleChanges = getChangesByTypename('TripScheduleChange', changes);

  return {
    price: priceChanges.length > 0 && getTripPriceChange(priceChanges[0]),
    schedule: scheduleChanges.length > 0 && getTripScheduleChanges(scheduleChanges, bounds),
  };
};

export const getTime = (dateValue) => dateUtil.format(dateValue, 'hh:mm');

export const getPriceNotificationContent = ({ tripChange = {}, higherText }) => {
  const { priceNew, priceOld } = tripChange.price;
  const differenceText = priceNew > priceOld ? higherText : null;
  const priceValue = Math.abs(priceNew - priceOld);
  return string.insertArgument(differenceText, price.formatPrice(priceValue));
};

const getChangedSegment = (segmentData) => (isDeparture, timeOld, timeNew) => ({
  isDeparture,
  timeOld,
  timeNew,
  ...segmentData,
});

export const getScheduleNotificationContent = (tripChange = {}) => {
  const { schedule } = tripChange;

  return schedule.map((bound) => ({
    title: bound.title,
    segments: bound.segments.reduce((acc, boundSegment) => {
      const getChangedSegmentInBound = getChangedSegment(boundSegment);
      const { departureTimeOld, departureTimeNew, arrivalTimeOld, arrivalTimeNew } = boundSegment;

      if (departureTimeNew) {
        acc.push(getChangedSegmentInBound(true, departureTimeOld, departureTimeNew));
      }

      if (arrivalTimeNew) {
        acc.push(getChangedSegmentInBound(false, arrivalTimeOld, arrivalTimeNew));
      }

      return acc;
    }, []),
  }));
};

export const isFreeCancellationDaysAheadValid = (departureDate, freeCancellationDaysAhead) => {
  const oneDay = 1000 * 60 * 60 * 24;
  const today = new Date();
  const todayMills = today.getTime();
  const departureDateMills = new Date(departureDate).getTime();
  const daysDifferenceMills = departureDateMills - todayMills;
  const daysDifference = Math.round(daysDifferenceMills / oneDay);

  return daysDifference - freeCancellationDaysAhead > 0;
};
